import { staircaseConfig, timing, variantForLevel } from './params';
const FEEDBACK_MS = timing.feedbackMs;
export function makeStimulus(trialIdx, deadlineMs, level) {
    const variant = variantForLevel(level);
    const span = Math.max(0, variant.preSignalMaxMs - variant.preSignalMinMs);
    const preSignalDelayMs = variant.preSignalMinMs + Math.floor(Math.random() * (span + 1));
    const clampedDeadline = Math.max(staircaseConfig.minValue, Math.min(staircaseConfig.maxValue, deadlineMs));
    const signalColor = Math.random() < 0.5 ? 'green' : 'red';
    return {
        trialIdx,
        preSignalDelayMs,
        deadlineMs: clampedDeadline,
        mode: variant.mode,
        signalColor,
    };
}
export function createReactionSportAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        lastRtMs: null,
        lastError: null,
        level,
        variant: variantForLevel(level),
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let signalStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function settle(resp, error) {
        if (!resolver)
            return;
        update({
            lastRtMs: resp.rtFromSignalMs > 0 ? resp.rtFromSignalMs : null,
            lastError: error,
        });
        const r = resolver;
        resolver = null;
        rejecter = null;
        r(resp);
    }
    function handleSignalInput(picked, mode, stim) {
        clearTimers();
        const rt = Date.now() - signalStart;
        const withinDeadline = rt <= stim.deadlineMs;
        let correct;
        let error = null;
        if (mode === 'simple') {
            correct = withinDeadline;
            if (!correct)
                error = 'late';
        }
        else {
            const sideMatch = picked !== null && picked === stim.signalColor;
            correct = withinDeadline && sideMatch;
            if (!withinDeadline)
                error = 'late';
            else if (!sideMatch)
                error = 'wrong_side';
        }
        settle({
            tapped: true,
            falseStart: false,
            rtFromSignalMs: rt,
            pickedSide: picked,
            correct,
        }, correct ? null : error);
    }
    return {
        mount() {
            // Idempotent — rendu declaratif cote React.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({
                    subPhase: 'fixation',
                    stim,
                    feedback: null,
                    lastError: null,
                });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'waiting_signal' });
                    timers.push(setTimeout(() => {
                        update({ subPhase: 'signal' });
                        signalStart = Date.now();
                        timers.push(setTimeout(() => {
                            // Timeout no-tap.
                            settle({
                                tapped: false,
                                falseStart: false,
                                rtFromSignalMs: 0,
                                pickedSide: null,
                                correct: false,
                            }, 'late');
                        }, timing.signalCapMs));
                    }, stim.preSignalDelayMs));
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, FEEDBACK_MS));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handleTap() {
            const stim = state.stim;
            if (!stim)
                return;
            if (state.subPhase === 'waiting_signal') {
                // False start.
                clearTimers();
                settle({
                    tapped: true,
                    falseStart: true,
                    rtFromSignalMs: 0,
                    pickedSide: null,
                    correct: false,
                }, 'false_start');
                return;
            }
            if (state.subPhase !== 'signal')
                return;
            if (stim.mode !== 'simple')
                return;
            handleSignalInput(null, 'simple', stim);
        },
        handlePick(side) {
            const stim = state.stim;
            if (!stim)
                return;
            if (state.subPhase === 'waiting_signal') {
                // False start aussi en mode choice2.
                clearTimers();
                settle({
                    tapped: true,
                    falseStart: true,
                    rtFromSignalMs: 0,
                    pickedSide: side,
                    correct: false,
                }, 'false_start');
                return;
            }
            if (state.subPhase !== 'signal')
                return;
            if (stim.mode !== 'choice2')
                return;
            handleSignalInput(side, 'choice2', stim);
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map